import React from 'react';
import {
  Search, Mail, Phone, Download, FlaskConical
} from 'lucide-react';
import { useSuperAdminContext } from '../SuperAdminContext';

const PEPTIDE_TAG = '[Peptide Business Inquiry]';

export default function InquiriesTab() {
  const ctx = useSuperAdminContext();
  const {
    activeTab,
    filteredInquiries,
    handleInquiryStatusChange,
    searchTerm,
    setSearchTerm,
    setStatusFilter,
    statusFilter,
    peptideOnly,
    setPeptideOnly,
    exportingInquiries,
    handleExportInquiriesCSV,
  } = ctx;

  if (activeTab !== 'inquiries') return null;

  const peptideCount = filteredInquiries.filter(
    (i) => (i.message || '').startsWith(PEPTIDE_TAG)
  ).length;

  return (
    <div className="inquiries-content" data-testid="inquiries-tab">
      <div className="content-header">
        <h2>
          {peptideOnly ? 'Peptide Leads' : 'All Inquiries'} ({filteredInquiries.length})
          {!peptideOnly && peptideCount > 0 && (
            <span
              data-testid="inquiries-peptide-count-pill"
              style={{
                marginLeft: '12px',
                fontSize: '0.8rem',
                background: 'rgba(74, 222, 128, 0.15)',
                color: '#4ade80',
                padding: '4px 12px',
                borderRadius: '999px',
                verticalAlign: 'middle',
              }}
            >
              <FlaskConical size={12} style={{ display: 'inline', marginRight: '4px' }} />
              {peptideCount} peptide
            </span>
          )}
        </h2>
        <div className="filters" style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
          <div className="search-box">
            <Search size={20} />
            <input
              type="text"
              placeholder="Search inquiries..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              data-testid="inquiries-search-input"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            data-testid="inquiries-status-filter"
          >
            <option value="all">All Status</option>
            <option value="new">New</option>
            <option value="contacted">Contacted</option>
            <option value="closed">Closed</option>
          </select>
          <button
            type="button"
            onClick={() => setPeptideOnly(!peptideOnly)}
            data-testid="inquiries-peptide-toggle"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              padding: '8px 16px',
              borderRadius: '8px',
              border: `1px solid ${peptideOnly ? 'rgba(74, 222, 128, 0.5)' : 'rgba(255,255,255,0.12)'}`,
              background: peptideOnly ? 'rgba(74, 222, 128, 0.12)' : 'transparent',
              color: peptideOnly ? '#4ade80' : '#e4e6eb',
              cursor: 'pointer',
              fontSize: '0.9rem',
              fontWeight: 500,
              transition: 'all 0.2s ease',
            }}
          >
            <FlaskConical size={16} />
            {peptideOnly ? 'Showing Peptide Only' : 'Peptide Only'}
          </button>
          <button
            type="button"
            onClick={handleExportInquiriesCSV}
            disabled={exportingInquiries}
            data-testid="inquiries-export-csv-btn"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              padding: '8px 16px',
              borderRadius: '8px',
              border: '1px solid rgba(59, 130, 246, 0.4)',
              background: 'rgba(59, 130, 246, 0.12)',
              color: '#60a5fa',
              cursor: exportingInquiries ? 'wait' : 'pointer',
              fontSize: '0.9rem',
              fontWeight: 500,
              opacity: exportingInquiries ? 0.6 : 1,
            }}
          >
            <Download size={16} />
            {exportingInquiries ? 'Exporting...' : 'Export CSV'}
          </button>
        </div>
      </div>

      <div className="inquiries-list">
        {filteredInquiries.length === 0 && (
          <div
            data-testid="inquiries-empty-state"
            style={{
              padding: '48px 24px',
              textAlign: 'center',
              color: '#9ca3af',
              background: 'rgba(255,255,255,0.02)',
              borderRadius: '12px',
              border: '1px dashed rgba(255,255,255,0.08)',
            }}
          >
            {peptideOnly
              ? 'No peptide leads yet. Leads from /peptides will appear here.'
              : 'No inquiries match your filters.'}
          </div>
        )}

        {filteredInquiries.map((inquiry) => {
          const isPeptide = (inquiry.message || '').startsWith(PEPTIDE_TAG);
          return (
            <div
              key={inquiry.id}
              className="inquiry-card"
              data-testid={`inquiry-card-${inquiry.id}`}
              style={isPeptide ? { borderLeft: '3px solid #4ade80' } : undefined}
            >
              <div className="inquiry-header">
                <div className="inquiry-info">
                  <h3 style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                    {inquiry.name}
                    {isPeptide && (
                      <span
                        data-testid="peptide-lead-badge"
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px',
                          fontSize: '0.7rem',
                          background: 'rgba(74, 222, 128, 0.15)',
                          color: '#4ade80',
                          padding: '3px 10px',
                          borderRadius: '999px',
                          fontWeight: 600,
                          letterSpacing: '0.03em',
                        }}
                      >
                        <FlaskConical size={11} />
                        PEPTIDE LEAD
                      </span>
                    )}
                  </h3>
                  <p className="inquiry-email">
                    <Mail size={16} /> {inquiry.email}
                  </p>
                  {inquiry.phone && (
                    <p className="inquiry-phone">
                      <Phone size={16} /> {inquiry.phone}
                    </p>
                  )}
                </div>
                <div className="inquiry-actions">
                  <select
                    value={inquiry.status}
                    onChange={(e) => handleInquiryStatusChange(inquiry.id, e.target.value)}
                    className={`status-select status-${inquiry.status}`}
                    data-testid={`inquiry-status-select-${inquiry.id}`}
                  >
                    <option value="new">New</option>
                    <option value="contacted">Contacted</option>
                    <option value="closed">Closed</option>
                  </select>
                  <span className="inquiry-date">
                    {new Date(inquiry.created_at).toLocaleDateString()}
                  </span>
                </div>
              </div>
              <div className="inquiry-message">
                <p style={{ whiteSpace: 'pre-wrap' }}>{inquiry.message}</p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
